// apps/merchant-v3/src/components/AvatarImage.tsx
//
// Bug cluster #5 — avatar/media URLs that 404 on the storage backend
// (deleted file, CF-cached stale 200 with corrupt 1x1 PNG, missing
// /opt/printway/storage/avatars/ dir on sandbox) previously rendered a
// broken <img>. This wrapper:
//
//   - calls resolveStorageUrl() to prefix relative `/storage/*` paths
//     with VITE_API_BASE_URL,
//   - tracks both an `onError` event AND a post-load
//     `naturalWidth <= 1` check (catches CF-cached corrupt PNGs that
//     load successfully but render 1x1),
//   - swaps to the supplied `fallback` (initials text, lucide icon)
//     when either signal fires.
//
// Reuse across ProfileDropdown, SettingsProfilePage, TeamMembersPage so
// the broken-state never reaches the user.

import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';

import { resolveStorageUrl } from '@/lib/format/storageUrl';
import { cn } from '@/lib/cn';

export interface AvatarImageProps {
  /** Raw storage path or absolute URL. Pass `undefined` to render the
   *  fallback directly without ever attempting an HTTP fetch. */
  src: string | null | undefined;
  /** Accessible alt — also feeds the broken-image tooltip. */
  alt: string;
  /** Rendered when src is empty OR when the image fails to load /
   *  renders smaller than 2 px (CF-cached corrupt PNG signal). */
  fallback: ReactNode;
  /** Tailwind classes applied to the outer wrapper. */
  className?: string;
  /** Tailwind classes applied to the inner <img>. */
  imgClassName?: string;
  /** data-testid pass-through. */
  testId?: string;
}

export function AvatarImage({
  src,
  alt,
  fallback,
  className,
  imgClassName,
  testId,
}: AvatarImageProps): ReactElement {
  const resolved = src ? resolveStorageUrl(src) : undefined;
  const [broken, setBroken] = useState<boolean>(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Reset broken when src changes — let the new URL re-try.
  useEffect(() => {
    setBroken(false);
  }, [resolved]);

  // Detect CF-cached 1x1 corrupt PNG even after a "successful" load.
  function handleLoad(): void {
    const img = imgRef.current;
    if (!img) return;
    if (img.naturalWidth <= 1 || img.naturalHeight <= 1) {
      setBroken(true);
    }
  }

  if (!resolved || broken) {
    return (
      <div
        data-testid={testId}
        title={alt}
        className={cn('inline-flex items-center justify-center', className)}
      >
        {fallback}
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      className={cn('inline-flex items-center justify-center overflow-hidden', className)}
    >
      <img
        ref={imgRef}
        src={resolved}
        alt={alt}
        loading="lazy"
        decoding="async"
        onError={() => setBroken(true)}
        onLoad={handleLoad}
        className={cn('h-full w-full object-cover', imgClassName)}
      />
    </div>
  );
}

export default AvatarImage;
